import { LinkProps } from "@remix-run/react";
import { cn } from "core/shared";
import { ReactNode } from "react";
import { Button } from "~/design-system/components/button";
import { PrimaryLink } from "~/design-system/components/link";

type ActionButtonProps = {
  label: string;
  onClick: () => void;
  to?: LinkProps["to"];
  className?: string;
};

type ActionLinkProps = {
  label: string;
  onClick?: () => void;
  to: LinkProps["to"];
  className?: string;
};

type ProductsSectionProps = {
  ref?: (node: HTMLElement | null) => void;
  title: {
    top: string;
    bottom?: string;
  };
  actionButton?: ActionButtonProps | ActionLinkProps;
  children: ReactNode;
  className?: string;
  classes?: {
    container?: string;
    title?: {
      top?: string;
      bottom?: string;
    };
  };
};

export function ProductsSection({
  ref,
  title,
  actionButton,
  children,
  className,
  classes,
}: ProductsSectionProps) {
  return (
    <section ref={ref} className={cn("bg-white", className)}>
      <div
        className={cn(
          "container flex flex-col gap-6 py-6 lg:gap-10 lg:py-8",
          classes?.container,
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-6">
          {/* Title */}
          <h2 className="flex max-w-[600px] flex-col gap-2 text-2xl font-bold text-gray-900 uppercase sm:text-3xl lg:text-5xl 2xl:text-7xl">
            {/* Title Top Part */}
            <span className={cn("text-primary-default", classes?.title?.top)}>
              {title.top}
            </span>
            {/* Title Bottom Part */}
            <span className={cn("text-black", classes?.title?.bottom)}>
              {title.bottom}
            </span>
          </h2>
          {/* Action Button */}
          {actionButton &&
            (actionButton.to ? (
              <PrimaryLink
                to={actionButton.to}
                onClick={actionButton.onClick}
                title={actionButton.label}
                aria-label={actionButton.label}
                className={cn(
                  "bg-primary-light hover:bg-primary-500 rounded-full border border-black text-xs font-semibold sm:text-sm lg:text-base",
                  actionButton.className,
                )}
              >
                {actionButton.label}
              </PrimaryLink>
            ) : (
              <Button
                title={actionButton.label}
                aria-label={actionButton.label}
                onClick={actionButton.onClick}
                className={cn(
                  "bg-primary-light hover:bg-primary-500 rounded-full border border-black px-8 py-3 text-xs font-semibold sm:text-sm lg:px-12 lg:py-4 lg:text-base",
                  actionButton.className,
                )}
              >
                {actionButton.label}
              </Button>
            ))}
        </div>
        {children}
      </div>
    </section>
  );
}
